<script>
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { 
    faChartLine,
    faExclamationTriangle,
    faCircleCheck,
    faCircleExclamation
  } from '@fortawesome/pro-light-svg-icons';
  import { isCompactMode } from '$stores/uiStore';

  export let stats = [];
  export let studyProperties = null;

  // Get the latest sensor data with at least 1000 readings
  $: latestStat = stats.length > 0 
    ? [...stats].reverse().find(stat => stat.total >= 1000) 
    : null;

  // Helper function to determine if CV is in good range (<36%)
  function isGoodCV(cv) {
    return cv < 36;
  }

  // Helper function to determine if TIR is in good range (>70%)
  function isGoodTIR(tir) {
    return tir > 70;
  }

  // Helper function to determine if TBR is in good range (<4%)
  function isGoodTBR(tbr) {
    return tbr < 4;
  }

  // Helper function to determine if TAR is in good range (<25%)
  function isGoodTAR(tar) {
    return tar < 25;
  }

  // Helper function to determine if events are in good range
  function isGoodEvents(hypo, hyper) {
    return hypo === 0 && hyper === 0;
  }
</script>

{#if latestStat}
  <div class="flex">
    <!-- <div class="flex items-center justify-center w-6">
      <span class="text-sm font-semibold text-gray-500 dark:text-gray-400 transform -rotate-90 whitespace-nowrap">Latest</span>
    </div> -->
    <div class="grid grid-cols-5 gap-2 px-1 mt-1 flex-1">
      <!-- CV% Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodCV(latestStat.cvPercent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodCV(latestStat.cvPercent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodCV(latestStat.cvPercent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Latest CV%</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{latestStat.cvPercent}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Glucose Variability</p>
        {/if}
      </div>

      <!-- TBR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodTBR(latestStat.veryLow.percent + latestStat.low.percent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodTBR(latestStat.veryLow.percent + latestStat.low.percent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodTBR(latestStat.veryLow.percent + latestStat.low.percent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Latest TBR</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{(latestStat.veryLow.percent + latestStat.low.percent).toFixed(1)}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Time Below Range</p>
        {/if}
      </div>

      <!-- TIR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodTIR(latestStat.target.percent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodTIR(latestStat.target.percent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodTIR(latestStat.target.percent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Latest TIR</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{latestStat.target.percent}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Time in Range</p>
        {/if}
      </div>

      <!-- TAR Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodTAR(latestStat.high.percent + latestStat.veryHigh.percent) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={isGoodTAR(latestStat.high.percent + latestStat.veryHigh.percent) ? faCircleCheck : faCircleExclamation} 
            class="text-xl {isGoodTAR(latestStat.high.percent + latestStat.veryHigh.percent) ? 'text-green-500' : 'text-yellow-500'} flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Latest TAR</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">{(latestStat.high.percent + latestStat.veryHigh.percent).toFixed(1)}%</p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Time Above Range</p>
        {/if}
      </div>

      <!-- Events Card -->
      <div class="bg-stone-200/50 dark:bg-gray-800/90 rounded-lg shadow p-4 border-l-2 {isGoodEvents(latestStat.hypoEvents, latestStat.hyperEvents) ? 'border-green-500' : 'border-yellow-500'}">
        <div class="flex items-center gap-2 mb-2">
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            class="text-xl text-yellow-500 flex-shrink-0"
          />
          <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300 whitespace-nowrap truncate">Latest Events</h3>
        </div>
        <p class="text-lg font-bold text-gray-900 dark:text-white">
          {latestStat.hypoEvents} Hypo / {latestStat.hyperEvents} Hyper
        </p>
        {#if !$isCompactMode}
          <p class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap truncate">Events >{(studyProperties?.glucoseParameters?.events?.eventDuration || 3) * 5} min</p>
        {/if}
      </div>
    </div>
  </div>
{/if} 